import React from 'react'
import { cn } from '../../utils/cn'

export interface RadioProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /**
   * Radio label
   */
  label?: string

  /**
   * Description text (below label)
   */
  description?: string
}

export const Radio = React.forwardRef<HTMLInputElement, RadioProps>(
  (
    {
      className,
      label,
      description,
      disabled,
      id,
      ...props
    },
    ref
  ) => {
    const radioId = id || `radio-${Math.random().toString(36).substr(2, 9)}`

    return (
      <div className="flex items-start gap-3">
        <div className="flex items-center h-5">
          <input
            ref={ref}
            type="radio"
            id={radioId}
            className={cn(
              'w-4 h-4 rounded-full transition-colors cursor-pointer',
              'border-2 border-soft-tan',
              'checked:bg-avocado-green checked:border-avocado-green',
              'focus:ring-2 focus:ring-toast-gold focus:ring-offset-0',
              disabled && 'opacity-50 cursor-not-allowed',
              className
            )}
            disabled={disabled}
            {...props}
          />
        </div>

        {(label || description) && (
          <div className="flex flex-col gap-1">
            {label && (
              <label
                htmlFor={radioId}
                className={cn(
                  'text-sm font-medium text-deep-charcoal cursor-pointer',
                  disabled && 'opacity-50 cursor-not-allowed'
                )}
              >
                {label}
              </label>
            )}

            {description && (
              <p className={cn(
                'text-xs text-deep-charcoal/60',
                disabled && 'opacity-50'
              )}>
                {description}
              </p>
            )}
          </div>
        )}
      </div>
    )
  }
)

Radio.displayName = 'Radio'

export default Radio
